import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Control, useWatch } from "react-hook-form";
import { CourseForm } from "../types/forms/course-form";
import {
  formatAdresse,
  isEnd,
  isRoutableTrip,
  isStart,
} from "../utils/course-stops";
import { ItineraryStep } from "../components/features/course/course-itinerary-section";

/** Laisse à l'utilisateur le temps de finir sa saisie avant de redessiner la carte. */
const DEBOUNCE_MS = 3000;

/**
 * Étapes affichées sur la carte, dérivées des arrêts du formulaire.
 *
 * Recalculées avec un délai : chaque frappe dans une adresse ferait sinon
 * repartir le rendu de l'itinéraire. Tant que le trajet n'est pas routable la
 * liste reste vide : les consommateurs (carte, itinéraire) ne montent alors pas
 * et n'interrogent pas le backend avec les arrêts vides du formulaire neuf.
 */
export const useItinerarySteps = (control: Control<CourseForm>) => {
  const t = useTranslations("stop_form");
  const [steps, setSteps] = useState<ItineraryStep[]>([]);
  const arrets = useWatch({ control, name: "arrets" });

  useEffect(() => {
    if (!arrets) return;

    if (!isRoutableTrip(arrets)) {
      // Immédiat, sans debounce : un trajet redevenu incomplet ne doit pas
      // laisser la carte tracer l'ancien itinéraire.
      setSteps((previous) => (previous.length ? [] : previous));
      return;
    }

    const total = arrets.length;
    const timer = setTimeout(() => {
      setSteps(
        arrets.map((stop, index) => ({
          type: isStart(index) ? "start" : isEnd(index, total) ? "end" : "step",
          label: t("step_label", { n: index + 1 }),
          name: formatAdresse(stop.adresse),
          adresse: stop.adresse,
        })),
      );
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [arrets, t]);

  return steps;
};
